import {createAsyncThunk} from '@reduxjs/toolkit';
import {getJWTToken} from '../../lib/auth-token';
import {handleAuthFailure, signOutExpiredSession} from '../../lib/auth-error-handler';
import {getWordlistApiUrl, apiFetch} from '../../lib/api';
import type {WordList} from '../../domain/types';
import {debugLog} from '../../lib/debug';

// API Response Types
interface WordlistResponse {
    created_at: string;
    description?: string;
    id: string;
    is_official: boolean;
    is_public: boolean;
    is_deleted: boolean;
    name: string;
    user_id: string;
    word_count: number;
    words: string[];
}

interface MarketplaceWordlistResponse {
    id: string;
    name: string;
    description?: string;
    word_count: number;
    created_at: string;
    user: {
        id: string;
        email: string;
    };
}

interface UserCollectionWordlistResponse extends WordlistResponse {
    is_owner?: boolean;
}

// Helper to convert API response to domain model
function convertToWordList(data: WordlistResponse | MarketplaceWordlistResponse | UserCollectionWordlistResponse, scope: 'global' | 'project' = 'global'): WordList {
    // Check if this is a marketplace format (has word_count instead of words)
  const isMarketplaceFormat = 'word_count' in data && !('words' in data);

    // Convert words to array if it's a string (API returns newline-separated string)
  let words: string[] = [];
  if (!isMarketplaceFormat && 'words' in data) {
    const wordsData = data.words as string[] | string | undefined;
    words = Array.isArray(wordsData)
      ? wordsData
      : (typeof wordsData === 'string' ? wordsData.split('\n').filter((w: string) => w.trim() !== '') : []);
  }

  // Extract marketplace-specific fields
  let creatorEmail: string | undefined;
  let userId: string | undefined;

  if (isMarketplaceFormat && 'user' in data) {
    const marketplaceData = data as MarketplaceWordlistResponse;
    creatorEmail = marketplaceData.user.email;
    userId = marketplaceData.user.id;
  } else if ('user_id' in data) {
    userId = data.user_id;
  }

  return {
    id: data.id,
    name: data.name,
    words,
    scope,
    createdAt: data.created_at,
    description: data.description || undefined,
    isPublic: 'is_public' in data ? data.is_public : false,
    isOfficial: 'is_official' in data ? data.is_official : false,
    isDeleted: 'is_deleted' in data ? data.is_deleted : false,
    creatorEmail,
    userId,
    isOwner: 'is_owner' in data ? data.is_owner : undefined,
    wordCount: 'word_count' in data ? data.word_count : undefined,
  };
}

/**
 * GET /wordlists - Fetch user's own wordlists (requires auth)
 */
export const fetchUserWordlists = createAsyncThunk(
  'wordlists/fetchUser',
  async (_, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWordlistApiUrl();
      const url = `${apiUrl}/wordlists`;

      debugLog('[Wordlists] Making GET request to:', url);
      const response = await apiFetch(url, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      debugLog('[Wordlists] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Wordlists] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Wordlist API returned error ${response.status}: ${errorText}`);
      }

            interface WordlistsResponse {
                wordlists: WordlistResponse[];
            }

            const data: WordlistsResponse = await response.json();
            debugLog('[Wordlists] Successfully fetched', data.wordlists.length, 'wordlists');

            const wordlists: WordList[] = data.wordlists.map(w => convertToWordList(w));
            return wordlists;
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * GET /wordlists/marketplace - Fetch public marketplace wordlists (requires auth)
 */
export const fetchMarketplaceWordlists = createAsyncThunk(
  'wordlists/fetchMarketplace',
  async (_, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWordlistApiUrl();
      const url = `${apiUrl}/wordlists/marketplace`;

      debugLog('[Wordlists] Making GET request to marketplace:', url);
      const response = await apiFetch(url, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      debugLog('[Wordlists] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Wordlists] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Wordlist API returned error ${response.status}: ${errorText}`);
      }

            interface MarketplaceResponse {
                wordlists: MarketplaceWordlistResponse[];
            }

            const data: MarketplaceResponse = await response.json();
            debugLog('[Wordlists] Successfully fetched', data.wordlists.length, 'marketplace wordlists');

            const wordlists: WordList[] = data.wordlists.map(w => convertToWordList(w));
            return wordlists;
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * GET /wordlists/:id - Fetch a specific wordlist (owner or public)
 */
export const fetchWordlistById = createAsyncThunk(
  'wordlists/fetchById',
  async (id: string, {rejectWithValue, dispatch}) => {
    try {
            // Try with auth first (for private wordlists)
      const jwt = await getJWTToken();

      const apiUrl = getWordlistApiUrl();
      const url = `${apiUrl}/wordlists/${id}`;

      debugLog('[Wordlists] Making GET request to:', url);

      const headers: Record<string, string> = {};
      if (jwt) {
        headers.Authorization = `Bearer ${jwt}`;
      }

      const response = await apiFetch(url, {headers});

      debugLog('[Wordlists] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Wordlists] Error response body:', errorText);

        if (response.status === 401 && jwt) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Wordlist API returned error ${response.status}: ${errorText}`);
      }


      const data: WordlistResponse = await response.json();
      debugLog('[Wordlists] Successfully fetched wordlist:', data.name);

      const wordlist: WordList = convertToWordList(data);
      return wordlist;
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * POST /wordlists - Create a new wordlist
 */
export const createWordlist = createAsyncThunk(
  'wordlists/create',
  async (
    params: {
            name: string;
            words: string[];
            description?: string;
            isPublic?: boolean;
        },
    {rejectWithValue, dispatch}
  ) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWordlistApiUrl();
      const url = `${apiUrl}/wordlists`;

      debugLog('[Wordlists] Making POST request to:', url);
      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: params.name,
          words: params.words,
          description: params.description || null,
          is_public: params.isPublic || false,
        }),
      });

      debugLog('[Wordlists] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Wordlists] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Wordlist API returned error ${response.status}: ${errorText}`);
      }

            interface CreateWordlistResponse {
                wordlist: WordlistResponse;
            }

            const data: CreateWordlistResponse = await response.json();
            debugLog('[Wordlists] Created wordlist:', data.wordlist.name);

            const wordlist: WordList = convertToWordList(data.wordlist);
            return wordlist;
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * PUT /wordlists/:id - Update a wordlist
 */
export const updateWordlist = createAsyncThunk(
  'wordlists/update',
  async (
    params: {
            id: string;
            name?: string;
            words?: string[];
            description?: string;
            isPublic?: boolean;
        },
    {rejectWithValue, dispatch}
  ) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWordlistApiUrl();
      const url = `${apiUrl}/wordlists/${params.id}`;

      debugLog('[Wordlists] Making PUT request to:', url);

            // Build request body with only provided fields
      const body: Record<string, any> = {};
      if (params.name !== undefined) body.name = params.name;
      if (params.words !== undefined) body.words = params.words;
      if (params.description !== undefined) body.description = params.description;
      if (params.isPublic !== undefined) body.isPublic = params.isPublic;

      const response = await apiFetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      debugLog('[Wordlists] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Wordlists] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Wordlist API returned error ${response.status}: ${errorText}`);
      }

            interface UpdateWordlistResponse {
                wordlist: WordlistResponse;
            }

            const data: UpdateWordlistResponse = await response.json();
            debugLog('[Wordlists] Updated wordlist:', data.wordlist.name);

            const wordlist: WordList = convertToWordList(data.wordlist);
            return wordlist;
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * DELETE /wordlists/:id - Delete a wordlist (owner only)
 */
export const deleteWordlist = createAsyncThunk(
  'wordlists/delete',
  async (id: string, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWordlistApiUrl();
      const url = `${apiUrl}/wordlists/${id}`;

      debugLog('[Wordlists] Making DELETE request to:', url);
      const response = await apiFetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      debugLog('[Wordlists] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Wordlists] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Wordlist API returned error ${response.status}: ${errorText}`);
      }

      debugLog('[Wordlists] Deleted wordlist successfully');
      return id;
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * GET /user/wordlists - Fetch user's wordlist collection (requires auth)
 * Returns all wordlists the user has chosen/added to their collection
 */
export const fetchUserWordlistsCollection = createAsyncThunk(
  'wordlists/fetchUserCollection',
  async (_, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWordlistApiUrl();
      const url = `${apiUrl}/user/wordlists`;

      debugLog('[Wordlists] Making GET request to user collection:', url);
      const response = await apiFetch(url, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      debugLog('[Wordlists] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Wordlists] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Wordlist API returned error ${response.status}: ${errorText}`);
      }

            interface UserCollectionResponse {
                wordlists: UserCollectionWordlistResponse[];
            }

            const data: UserCollectionResponse = await response.json();
            debugLog('[Wordlists] Successfully fetched', data.wordlists.length, 'collection wordlists');

            const wordlists: WordList[] = data.wordlists.map(w => convertToWordList(w));
            return wordlists;
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * POST /user/wordlists - Add a wordlist to user's collection (requires auth)
 */
export const addWordlistToCollection = createAsyncThunk(
  'wordlists/addToCollection',
  async (wordlistId: string, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWordlistApiUrl();
      const url = `${apiUrl}/user/wordlists`;

      debugLog('[Wordlists] Making POST request to add to collection:', url);
      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          wordlistId,
        }),
      });

      debugLog('[Wordlists] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Wordlists] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Wordlist API returned error ${response.status}: ${errorText}`);
      }

            interface AddToCollectionResponse {
                success: boolean;
                message: string;
                association: {
                  id: string;
                  user_id: string;
                  wordlist_id: string;
                  created_at: string;
                };
            }

            const data: AddToCollectionResponse = await response.json();
            debugLog('[Wordlists] Added wordlist to collection:', data.message);

            // The response only contains the association, we need to fetch the full wordlist data
            // First, refresh the collection to get updated data
            await dispatch(fetchUserWordlistsCollection()).unwrap();

            // Then fetch the specific wordlist details
            const wordlistResult = await dispatch(fetchWordlistById(wordlistId)).unwrap();
            return wordlistResult;
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * DELETE /user/wordlists/:id - Remove a wordlist from user's collection (requires auth)
 */
export const removeWordlistFromCollection = createAsyncThunk(
  'wordlists/removeFromCollection',
  async (wordlistId: string, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWordlistApiUrl();
      const url = `${apiUrl}/user/wordlists/${wordlistId}`;

      debugLog('[Wordlists] Making DELETE request to remove from collection:', url);
      const response = await apiFetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      debugLog('[Wordlists] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Wordlists] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Wordlist API returned error ${response.status}: ${errorText}`);
      }

      debugLog('[Wordlists] Removed wordlist from collection successfully');
      return wordlistId;
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);
